import React, { useCallback, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { Card, Select } from 'antd';
import ReactECharts from 'echarts-for-react';

import { DATA_SERVICES_SORT_OPTIONS } from '@/shared/lib/constants/options';
import { escapeHtml } from '@/shared/lib/utils';
import type { DataServiceSortOption } from '@/shared/types/options';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';

import { type TagDistributionApiResponse, useTagDistribution } from '@/features/tag-distribution';

import styles from './TagDistributionOverview.module.css';

const CHART_CONFIG = {
  BAR_HEIGHT: 20,
  BAR_SPACING: 32,
  BAR_CATEGORY_GAP: 16,
  MIN_HEIGHT: 200,
  MAX_VALUE: 100,
  BORDER_RADIUS: 8,
  GRID_PADDING: '5%',
  GRID_OFFSET: 10,
} as const;

const AXIS_CONFIG = {
  LABEL_WIDTH: 120,
  LABEL_FONT_SIZE: 14,
  LABEL_MARGIN: 20,
} as const;

const LABEL_CONFIG = {
  FONT_SIZE: 14,
  DISTANCE: 5,
} as const;

const COLORS = {
  BACKGROUND: '#f5f5f5',
  TEXT: '#292929',
} as const;

type TagData = TagDistributionApiResponse['data'][number];

const TAG_SORT_HANDLERS: Record<DataServiceSortOption, (a: TagData, b: TagData) => number> = {
  countAsc: (a, b) => {
    const diff = a.count - b.count;
    return diff !== 0 ? diff : a.tag.localeCompare(b.tag, 'ko', { numeric: true });
  },
  countDesc: (a, b) => {
    const diff = b.count - a.count;
    return diff !== 0 ? diff : a.tag.localeCompare(b.tag, 'ko', { numeric: true });
  },
  nameAsc: (a, b) => a.tag.localeCompare(b.tag, 'ko', { numeric: true }),
};

const getSortedTagData = (
  tagData: TagDistributionApiResponse['data'],
  sortOption: DataServiceSortOption
): TagDistributionApiResponse['data'] => {
  if (!tagData.length) return [];
  return [...tagData].sort(TAG_SORT_HANDLERS[sortOption]);
};

export const TagDistributionOverview: React.FC = () => {
  const navigate = useNavigate();
  const { data } = useTagDistribution();
  const tagData = useMemo(() => data?.data || [], [data]);

  const [sortOption, setSortOption] = useState<DataServiceSortOption>('countDesc');
  const onChangeSortOption = useCallback((value: DataServiceSortOption) => {
    setSortOption(value);
  }, []);

  const sortedTagData = useMemo(() => {
    return getSortedTagData(tagData, sortOption);
  }, [tagData, sortOption]);

  const maxCount = useMemo(() => {
    return sortedTagData.length > 0 ? Math.max(...sortedTagData.map((item) => item.count)) : 0;
  }, [sortedTagData]);

  const getRelativePercentage = useCallback(
    (count: number) => {
      if (maxCount > 0) {
        return (count / maxCount) * CHART_CONFIG.MAX_VALUE;
      }
      return 0;
    },
    [maxCount]
  );

  const handleChartClick = useCallback(
    (params: { dataIndex?: number }) => {
      if (params.dataIndex === undefined) {
        return;
      }

      const clickedTag = sortedTagData[params.dataIndex];
      if (clickedTag) {
        navigate(`/search?tags=${encodeURIComponent(clickedTag.tagFQN)}`);
      }
    },
    [sortedTagData, navigate]
  );

  const chartOption = useMemo(() => {
    return {
      animation: false,
      grid: {
        left: CHART_CONFIG.GRID_PADDING,
        right: CHART_CONFIG.GRID_PADDING,
        top: CHART_CONFIG.GRID_OFFSET,
        bottom: CHART_CONFIG.GRID_OFFSET,
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        max: CHART_CONFIG.MAX_VALUE,
        axisLabel: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'category',
        data: sortedTagData.map((item) => item.tag),
        triggerEvent: true,
        axisLabel: {
          show: true,
          fontSize: AXIS_CONFIG.LABEL_FONT_SIZE,
          fontFamily: 'NanumSquare',
          color: COLORS.TEXT,
          width: AXIS_CONFIG.LABEL_WIDTH,
          overflow: 'truncate',
          interval: 0,
          margin: AXIS_CONFIG.LABEL_MARGIN,
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        inverse: true,
      },
      series: [
        {
          name: 'background',
          type: 'bar',
          barWidth: CHART_CONFIG.BAR_HEIGHT,
          barGap: '-100%',
          data: sortedTagData.map(() => ({
            value: CHART_CONFIG.MAX_VALUE,
            itemStyle: {
              color: COLORS.BACKGROUND,
              borderRadius: [
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
              ],
            },
          })),
          animation: false,
          silent: true,
        },
        {
          name: 'data',
          type: 'bar',
          barWidth: CHART_CONFIG.BAR_HEIGHT,
          data: sortedTagData.map((item) => ({
            value: getRelativePercentage(item.count),
            itemStyle: {
              color: item.color,
              borderRadius: [
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
              ],
            },
          })),
          label: {
            show: true,
            position: 'right',
            formatter: (params: { dataIndex: number; value: number }) => {
              const item = sortedTagData[params.dataIndex];
              return item?.count?.toLocaleString() || '0';
            },
            fontSize: LABEL_CONFIG.FONT_SIZE,
            fontFamily: 'NanumSquare',
            color: COLORS.TEXT,
            distance: LABEL_CONFIG.DISTANCE,
          },
          silent: true,
        },
        {
          name: 'clickArea',
          type: 'bar',
          barWidth: CHART_CONFIG.BAR_HEIGHT,
          barGap: '-100%',
          z: 10,
          data: sortedTagData.map(() => ({
            value: CHART_CONFIG.MAX_VALUE,
            itemStyle: {
              color: 'transparent',
              borderColor: 'transparent',
              borderRadius: [
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
                CHART_CONFIG.BORDER_RADIUS,
              ],
            },
          })),
          cursor: 'pointer',
          emphasis: {
            itemStyle: {
              color: 'rgba(0, 0, 0, 0.05)',
            },
          },
          animation: false,
        },
      ],
      tooltip: {
        show: true,
        trigger: 'item',
        confine: true,
        backgroundColor: '#FFFFFFCC',
        textStyle: {
          color: '#101010',
          fontSize: 12,
          fontFamily: 'NanumSquare',
          fontWeight: 400,
        },
        borderWidth: 1,
        borderColor: '#E6E6E6',
        borderRadius: 8,
        padding: [16],
        extraCssText:
          'backdrop-filter: blur(8px); box-shadow: none; width: 240px !important; white-space: normal !important; overflow: visible !important;',
        formatter: (params: { seriesName?: string; dataIndex?: number }) => {
          if (params.seriesName !== 'clickArea' || params.dataIndex === undefined) {
            return '';
          }
          const item = sortedTagData[params.dataIndex];
          if (!item) return '';

          return `
            <div style="
              display: flex;
              flex-direction: row;
              align-items: center;
              gap: 16px;
              width: 100%;
            ">
              <div style="
                font-family: NanumSquare;
                font-size: 12px;
                line-height: 16px;
                font-weight: 400;
                letter-spacing: 0;
                color: #101010;
                flex: 1 1 0%;
                min-width: 0;
                max-width: 100%;
                word-break: break-word;
                overflow-wrap: break-word;
                white-space: normal;
              ">${escapeHtml(item.tag)}</div>
              <div style="
                font-family: NanumSquare;
                font-size: 12px;
                line-height: 16px;
                font-weight: 400;
                letter-spacing: 0;
                color: #101010;
                white-space: nowrap;
                flex-shrink: 0;
              ">${item.count.toLocaleString()}</div>
            </div>
          `;
        },
      },
    };
  }, [getRelativePercentage, sortedTagData]);

  const rechartHeight = useMemo(() => {
    return sortedTagData.length * CHART_CONFIG.BAR_SPACING + CHART_CONFIG.GRID_OFFSET;
  }, [sortedTagData]);

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div className={styles.title}>태그별 자산 분포</div>
        </div>
        <Select
          options={DATA_SERVICES_SORT_OPTIONS}
          value={sortOption}
          onChange={onChangeSortOption}
          className={styles.sortSelect}
        />
      </div>

      <NoDataBoundary data={sortedTagData} emptyDescription="표시할 데이터가 없습니다">
        <div className={styles.chartContainer}>
          <ReactECharts
            option={chartOption}
            opts={{ renderer: 'canvas', height: rechartHeight, width: 'auto' }}
            className={styles.chart}
            onEvents={{
              click: handleChartClick,
            }}
          />
        </div>
      </NoDataBoundary>
    </Card>
  );
};
